import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  FlaskConical, Loader2, Play, ArrowRight, TrendingDown, TrendingUp, Minus, Sparkles, Bookmark,
  Scale, Trophy, Trash2, Check,
} from 'lucide-react';
import { toast } from 'sonner';
import ProcessHelp from '@/components/ProcessHelp';

const SEV_STYLE = {
  high: 'bg-rose-500/15 border-rose-500/40 text-rose-600 dark:text-rose-300',
  medium: 'bg-amber-500/15 border-amber-500/40 text-amber-600 dark:text-amber-300',
  low: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-300',
};
const DIR = {
  improved: { cls: 'text-emerald-600 dark:text-emerald-300', Icon: TrendingDown, label: 'improved' },
  worsened: { cls: 'text-rose-600 dark:text-rose-300', Icon: TrendingUp, label: 'worsened' },
  unchanged: { cls: 'text-muted-foreground', Icon: Minus, label: 'no change' },
};
const LEVERS = [
  { key: 'rate_change_pct', label: 'Rate change', hint: 'Higher rates raise earned premium → lowers loss ratio', unit: '%' },
  { key: 'loss_trend_pct', label: 'Loss trend', hint: 'Scales incurred loss up or down', unit: '%' },
  { key: 'frequency_change_pct', label: 'Frequency change', hint: 'Scales claim count', unit: '%' },
  { key: 'reserve_strengthen_pct', label: 'Reserve strengthening', hint: 'Reduces the reserve shortfall', unit: '%' },
];
const EMPTY = { rate_change_pct: '', loss_trend_pct: '', frequency_change_pct: '', reserve_strengthen_pct: '', scope_lob: '' };

function LeverList({ levers }) {
  const items = LEVERS.filter(({ key }) => Number(levers?.[key]))
    .map(({ key, label }) => `${label} ${levers[key] > 0 ? '+' : ''}${levers[key]}%`);
  if (levers?.scope_lob) items.push(`Scope: ${levers.scope_lob}`);
  return <div className="text-[11px] text-muted-foreground">{items.length ? items.join(' · ') : 'No levers set'}</div>;
}

function CompareCol({ s, win, onAttach, modelLabel }) {
  const im = s.impact || {};
  return (
    <div className={`rounded-xl border p-3 space-y-2 ${win ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-border/50 bg-card/40'}`} data-testid={`scn-compare-col-${s.scenario_id}`}>
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium truncate">{s.name}</span>
        {win ? <Badge variant="outline" className="border-emerald-500/50 text-emerald-600 dark:text-emerald-300 gap-1 text-[10px]"><Trophy className="w-3 h-3" /> Resolves more</Badge> : null}
      </div>
      {modelLabel ? <div className="text-[10px] text-muted-foreground -mt-1">{modelLabel}</div> : null}
      <LeverList levers={s.levers} />
      <div className="flex items-center gap-2 text-xs flex-wrap">
        <Badge variant="outline" className={im.resolved > 0 ? SEV_STYLE.low : 'border-border/40 text-muted-foreground'}>Firing {im.firing_before} → {im.firing_after}</Badge>
        {im.resolved > 0 ? <span className="text-emerald-500 text-[11px]">{im.resolved} resolved</span> : null}
        {im.worsened > 0 ? <span className="text-rose-500 text-[11px]">{im.worsened} worsened</span> : null}
      </div>
      <div className="space-y-1 pt-1 border-t border-border/30">
        {(s.signals || []).map((sig) => (
          <div key={sig.signal_type} className="flex items-center justify-between gap-2 text-[11px]">
            <span className="text-muted-foreground truncate">{sig.title}</span>
            <span className="text-foreground shrink-0">{sig.firing_before} → {sig.firing_after}</span>
          </div>
        ))}
      </div>
      {win && onAttach ? (
        <Button size="sm" variant="outline" className="w-full h-7 text-[11px] gap-1 border-emerald-500/40 text-emerald-600 dark:text-emerald-300"
          onClick={() => onAttach(s)} data-testid={`scn-attach-${s.scenario_id}`}>
          Attach to approval <ArrowRight className="w-3 h-3" />
        </Button>
      ) : null}
    </div>
  );
}

function ScenarioCompare({ items, onAttach, labelFor }) {
  const list = (items || []).filter(Boolean);
  if (list.length < 2) return null;
  const best = Math.max(...list.map((s) => s.impact?.resolved || 0));
  const winners = list.filter((s) => (s.impact?.resolved || 0) === best && best > 0).length === 1;
  const cols = list.length >= 3 ? 'md:grid-cols-3' : 'md:grid-cols-2';
  return (
    <div className={`grid grid-cols-1 ${cols} gap-3`} data-testid="scn-compare-panel">
      {list.map((s) => (
        <CompareCol key={s.scenario_id} s={s} win={winners && (s.impact?.resolved || 0) === best}
          onAttach={onAttach} modelLabel={labelFor ? labelFor(s.model_id) : null} />
      ))}
    </div>
  );
}

export default function ScenarioSimulatorPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState(params.get('model_id') || '');
  const [lobs, setLobs] = useState([]);
  const prefillLevers = () => {
    const l = { ...EMPTY };
    let any = false;
    LEVERS.forEach(({ key }) => { const v = params.get(key); if (v != null && v !== '') { l[key] = v; any = true; } });
    const scope = params.get('scope_lob'); if (scope) { l.scope_lob = scope; any = true; }
    return any ? l : EMPTY;
  };
  const [levers, setLevers] = useState(prefillLevers);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [scnName, setScnName] = useState('');
  const [savingScn, setSavingScn] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [cmp, setCmp] = useState([]);
  const [scnScope, setScnScope] = useState('all');
  const prevModelId = useRef(params.get('model_id') || '');

  useEffect(() => {
    api.getSemanticModels().then((r) => {
      const ms = r.models || r || [];
      setModels(ms);
      if (!modelId && ms.length) setModelId(ms[0].model_id);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLobs = useCallback(async () => {
    if (!modelId) return;
    try {
      const r = await api.getSignalThresholds(modelId);
      setLobs(r.lobs || []);
    } catch { setLobs([]); }
  }, [modelId]);

  useEffect(() => { loadLobs(); if (prevModelId.current && prevModelId.current !== modelId) { setResult(null); setLevers(EMPTY); } prevModelId.current = modelId; }, [loadLobs]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadScenarios = useCallback(async () => {
    if (scnScope === 'this' && !modelId) { setScenarios([]); return; }
    try { const r = await api.getScenarios(scnScope === 'all' ? '' : modelId); setScenarios(r.scenarios || []); } catch { setScenarios([]); }
  }, [scnScope, modelId]);
  useEffect(() => { loadScenarios(); setCmp([]); }, [loadScenarios]);
  const modelLabel = (mid) => (models.find((m) => m.model_id === mid) || {}).name || mid;

  const toggleCmp = (id) => setCmp((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : (prev.length >= 3 ? [...prev.slice(1), id] : [...prev, id])));
  const deleteScn = async (id) => {
    try { await api.deleteScenario(id); toast.success('Scenario deleted'); setCmp((p) => p.filter((x) => x !== id)); loadScenarios(); }
    catch (e) { toast.error(e.message || 'Delete failed'); }
  };

  const setLever = (k, v) => setLevers((l) => ({ ...l, [k]: v }));

  const buildPayload = () => {
    const payload = {};
    LEVERS.forEach(({ key }) => { const n = parseFloat(levers[key]); if (!Number.isNaN(n)) payload[key] = n; });
    payload.scope_lob = levers.scope_lob || '';
    return payload;
  };

  const run = async () => {
    if (!modelId) return;
    const payload = buildPayload();
    if (!Object.keys(payload).some((k) => k !== 'scope_lob' && payload[k])) {
      toast.warning('Set at least one lever to a non-zero value');
      return;
    }
    setRunning(true);
    try {
      const r = await api.simulateScenario(modelId, payload);
      setResult(r);
      if (r?.impact?.no_data) toast.warning('This model has no actuarial signals to simulate.');
      else toast.success(`Firing lines: ${r.impact.firing_before} → ${r.impact.firing_after}`);
    } catch (e) {
      toast.error(e.message || 'Simulation failed');
    } finally {
      setRunning(false);
    }
  };

  const saveScenario = async () => {
    if (!modelId || !scnName.trim()) return;
    setSavingScn(true);
    try {
      await api.saveScenario(modelId, scnName.trim(), buildPayload());
      toast.success(`Scenario "${scnName.trim()}" saved — attach it when submitting a recommendation`);
      setScnName('');
      loadScenarios();
    } catch (e) {
      toast.error(e.message || 'Failed to save scenario');
    } finally {
      setSavingScn(false);
    }
  };

  const impact = result?.impact;
  const allZero = LEVERS.every(({ key }) => !Number(levers[key]));

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="scenario-simulator-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-heading font-semibold flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-primary" /> What-if Analysis
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
            Model a &quot;what-if&quot; — a rate change, loss trend, frequency shift or reserve strengthening — and see the expected impact on your risk signals <span className="text-foreground">before</span> you recommend anything. Deterministic, computed on your own governed data. Nothing is executed.
          </p>
        </div>
        <ProcessHelp
          testId="scenario-help"
          title="What-if Analysis & Compare"
          section="scenario-simulator"
          intro="Model a what-if and see the projected impact on your risk signals before recommending anything — deterministic and never executed."
          steps={[
            'Launch from a recommendation to prefill the scope and a suggested lever, or pick a model here.',
            'Set one or more levers to a non-zero value (all-zero shows a "no change from baseline" hint).',
            'Click Run to see which signals resolve or worsen — a lever with no modelled effect on a signal says so instead of showing a false 0 → 0.',
            'Save promising alternatives; tick up to three saved alternatives to compare side by side.',
            'On the winning alternative click Attach to approval to carry its impact into a material recommendation as rationale.',
          ]}
        />
      </div>

      <div className="rounded-xl border border-border/50 bg-card/40 p-4 space-y-4" data-testid="sim-panel">
        <div className="flex items-center gap-2 flex-wrap">
          <Label className="text-xs text-muted-foreground">Model</Label>
          <select value={modelId} onChange={(e) => setModelId(e.target.value)}
            className="h-8 rounded-md border border-border/50 bg-background px-2 text-xs" data-testid="sim-model-select">
            {models.map((m) => <option key={m.model_id} value={m.model_id}>{m.name || m.model_id}</option>)}
          </select>
          <Label className="text-xs text-muted-foreground ml-2">Scope</Label>
          <select value={levers.scope_lob} onChange={(e) => setLever('scope_lob', e.target.value)}
            className="h-8 rounded-md border border-border/50 bg-background px-2 text-xs" data-testid="sim-scope-select">
            <option value="">All lines of business</option>
            {lobs.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {LEVERS.map(({ key, label, hint, unit }) => (
            <div key={key} className="rounded-lg border border-border/40 bg-background/40 p-3 space-y-1.5" data-testid={`sim-lever-${key}`}>
              <div className="text-xs font-medium text-foreground">{label}</div>
              <div className="flex items-center gap-2">
                <Input type="number" value={levers[key]} onChange={(e) => setLever(key, e.target.value)}
                  placeholder="0" className="h-8 text-xs" data-testid={`sim-input-${key}`} />
                <span className="text-[11px] text-muted-foreground">{unit}</span>
              </div>
              <p className="text-[10px] text-muted-foreground leading-snug">{hint}</p>
            </div>
          ))}
        </div>
        <div className="flex justify-end">
          <Button size="sm" onClick={run} disabled={running || !modelId} className="gap-1 text-xs h-8" data-testid="sim-run">
            {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />} Run simulation
          </Button>
        </div>
        {allZero ? (
          <p className="text-[11px] text-muted-foreground text-right -mt-2" data-testid="sim-baseline-hint">
            No change from baseline — set at least one lever to a non-zero value to model a what-if.
          </p>
        ) : null}
      </div>

      {result && !impact?.no_data && (
        <div className="space-y-4" data-testid="sim-results">
          <div className="flex items-center gap-2 flex-wrap rounded-xl border border-cyan-500/30 bg-cyan-500/5 p-3" data-testid="sim-save-row">
            <Bookmark className="w-4 h-4 text-cyan-400" />
            <span className="text-xs text-foreground">Save this scenario to attach as rationale on a recommendation approval:</span>
            <Input value={scnName} onChange={(e) => setScnName(e.target.value)} placeholder="Name this scenario"
              className="h-8 text-xs w-52" data-testid="sim-scn-name" />
            <Button size="sm" onClick={saveScenario} disabled={savingScn || !scnName.trim()} className="h-8 text-xs gap-1" data-testid="sim-scn-save">
              {savingScn ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bookmark className="w-3 h-3" />} Save scenario
            </Button>
          </div>
          <div className="flex items-center gap-3 flex-wrap rounded-xl border border-border/50 bg-card/40 p-3" data-testid="sim-impact">
            <span className="text-sm font-medium text-foreground">Expected impact</span>
            <Badge variant="outline" className={impact.resolved > 0 ? SEV_STYLE.low : 'border-border/40 text-muted-foreground'}>
              Firing lines: {impact.firing_before} <ArrowRight className="w-3 h-3 inline mx-0.5" /> {impact.firing_after}
            </Badge>
            {impact.resolved > 0 && <span className="text-emerald-500 text-xs">{impact.resolved} resolved</span>}
            {impact.worsened > 0 && <span className="text-rose-500 text-xs">{impact.worsened} worsened</span>}
            {(result.prioritized || []).length > 0 && (
              <span className="text-[11px] text-muted-foreground flex items-center gap-1 ml-auto" data-testid="sim-top-lever">
                <Sparkles className="w-3 h-3 text-violet-400" /> Biggest impact: <span className="text-foreground">{result.prioritized[0].label}</span> ({result.prioritized[0].value > 0 ? '+' : ''}{result.prioritized[0].value}%, {result.prioritized[0].resolved} resolved)
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {result.signals.map((s) => (
              <div key={s.signal_type} className="rounded-xl border border-border/40 bg-card/40 p-4 space-y-2" data-testid={`sim-signal-${s.signal_type}`}>
                <div className="flex items-center justify-between gap-2">
                  <h2 className="text-sm font-medium text-foreground">{s.title}</h2>
                </div>
                {s.overall_before && (
                  <div className="flex items-center gap-2 text-xs" data-testid={`sim-overall-${s.signal_type}`}>
                    <span className="text-muted-foreground">{s.overall_before.label}:</span>
                    <span className="text-foreground font-medium">{s.overall_before.display}</span>
                    <ArrowRight className="w-3 h-3 text-muted-foreground" />
                    <span className="text-foreground font-semibold">{s.overall_after?.display}</span>
                  </div>
                )}
                {s.supported === false ? (
                  <p className="text-[11px] text-amber-600 dark:text-amber-300 rounded-md bg-amber-500/10 border border-amber-500/30 px-2 py-1" data-testid={`sim-unsupported-${s.signal_type}`}>
                    {s.support_note || 'The levers you set have no modelled effect on this signal.'}
                  </p>
                ) : (
                  <div className="text-[11px] text-muted-foreground">Firing: {s.firing_before} → {s.firing_after} · status {s.status_before} → {s.status_after}</div>
                )}
                <div className="space-y-1">
                  {(s.changes || []).map((c, i) => {
                    const d = DIR[c.direction] || DIR.unchanged;
                    const DIcon = d.Icon;
                    return (
                      <div key={i} className="flex items-center justify-between gap-2 text-[11px] rounded-md px-2 py-1 bg-background/40" data-testid={`sim-change-${s.signal_type}-${i}`}>
                        <span className="text-foreground font-medium min-w-0 truncate">{c.label}</span>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-muted-foreground">{c.before_display}</span>
                          <ArrowRight className="w-3 h-3 text-muted-foreground" />
                          <span className="text-foreground font-semibold">{c.after_display}</span>
                          <Badge variant="outline" className={`text-[8px] ${SEV_STYLE[c.after_severity] || SEV_STYLE.low}`}>{c.after_severity}</Badge>
                          <span className={`inline-flex items-center gap-0.5 ${d.cls}`}><DIcon className="w-3 h-3" /></span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {(result.prioritized || []).length > 0 && (
            <div className="rounded-xl border border-border/40 bg-card/40 p-4" data-testid="sim-prioritized">
              <h2 className="text-sm font-medium text-foreground mb-2">Lever priority (marginal impact)</h2>
              <div className="space-y-1">
                {result.prioritized.map((p) => (
                  <div key={p.lever} className="flex items-center justify-between text-[11px] rounded-md px-2 py-1 bg-background/40" data-testid={`sim-prio-${p.lever}`}>
                    <span className="text-foreground font-medium">{p.label} ({p.value > 0 ? '+' : ''}{p.value}%)</span>
                    <span className="text-muted-foreground">{p.firing_before} → {p.firing_after} firing · <span className="text-emerald-500">{p.resolved} resolved</span></span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {result && impact?.no_data && (
        <div className="text-sm text-muted-foreground py-10 text-center" data-testid="sim-no-data">
          This model doesn&apos;t expose the actuarial fields needed to simulate risk signals. Try the Preview SQL Sample model.
        </div>
      )}

      {(scenarios.length > 0 || scnScope === 'this') && (
        <div className="space-y-3" data-testid="scenario-compare">
          <div className="flex items-center gap-2 flex-wrap">
            <Scale className="w-4 h-4 text-cyan-400" />
            <h2 className="text-sm font-medium">Saved alternatives</h2>
            <span className="text-[11px] text-muted-foreground">Tick up to three to compare side by side{scnScope === 'all' ? ' — even across different models' : ''}.</span>
            <div className="flex items-center gap-1 ml-auto rounded-lg border border-border/50 p-0.5">
              <button onClick={() => setScnScope('all')} className={`text-[11px] px-2 py-0.5 rounded-md ${scnScope === 'all' ? 'bg-cyan-500/15 text-cyan-600 dark:text-cyan-300' : 'text-muted-foreground'}`} data-testid="scn-scope-all">All models</button>
              <button onClick={() => setScnScope('this')} className={`text-[11px] px-2 py-0.5 rounded-md ${scnScope === 'this' ? 'bg-cyan-500/15 text-cyan-600 dark:text-cyan-300' : 'text-muted-foreground'}`} data-testid="scn-scope-this">This model</button>
            </div>
          </div>
          {scenarios.length === 0 ? (
            <p className="text-[11px] text-muted-foreground py-2" data-testid="scn-empty">No saved scenarios for this model yet — switch to “All models”, or run a simulation and save one.</p>
          ) : null}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {scenarios.map((s) => {
              const on = cmp.includes(s.scenario_id);
              return (
                <div key={s.scenario_id} className={`rounded-lg border p-3 ${on ? 'border-cyan-500/50 bg-cyan-500/5' : 'border-border/40 bg-card/40'}`} data-testid={`scn-card-${s.scenario_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <button onClick={() => toggleCmp(s.scenario_id)} className="text-left flex items-start gap-2 min-w-0" data-testid={`scn-compare-toggle-${s.scenario_id}`}>
                      <span className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? 'bg-cyan-500 border-cyan-500' : 'border-border'}`}>{on ? <Check className="w-3 h-3 text-white" /> : null}</span>
                      <span className="min-w-0">
                        <span className="text-sm font-medium block truncate">{s.name}</span>
                        <span className="block text-[10px] text-cyan-600 dark:text-cyan-300/80 truncate">{modelLabel(s.model_id)}</span>
                        <span className="block text-[11px] text-muted-foreground truncate">{s.headline}</span>
                      </span>
                    </button>
                    <button onClick={() => deleteScn(s.scenario_id)} className="text-muted-foreground hover:text-rose-500 shrink-0" data-testid={`scn-delete-${s.scenario_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              );
            })}
          </div>
          {cmp.length >= 2 && <ScenarioCompare items={cmp.map((id) => scenarios.find((s) => s.scenario_id === id))} labelFor={modelLabel} onAttach={(s) => navigate(`/admin/recommendations?attach_scenario=${encodeURIComponent(s.scenario_id)}&model_id=${encodeURIComponent(s.model_id || modelId)}`)} />}
        </div>
      )}
    </div>
  );
}

export { ScenarioSimulatorPage };
